import { slurp } from './util.js';
import { normaliseWave, getWaveFunction } from './wave-things.js';

export const SAMPLE_RATE = 44100;
export const baseFrequency = 220;

let audioContext = null;

function getAudioContext() {
    if (audioContext === null) {
        const AudioContext = window.AudioContext || window.webkitAudioContext || false;
        if (!AudioContext) {
            // Web Audio API not supported :(
            return null;
        }
        audioContext = new AudioContext();
    }
    return audioContext;
}

/**
 *
 * @param {function(number):number|Array<number>} wave
 */
export function playSoundWave(wave) {
    if (wave.length == 0) {
        // Do nothing if we have a nothing-lengthed wave.
        return;
    }
    const baseVolume = 0.8;
    const decay = 3;
    if (wave.constructor === Array) {
        // transform our wave array into a function we can call
        wave = getWaveFunction(normaliseWave(wave));
    }

    const audioContext = getAudioContext();
    if (audioContext === null) {
        return false;
    }
    const buffer = audioContext.createBuffer(1, SAMPLE_RATE, SAMPLE_RATE);

    const channel = buffer.getChannelData(0);
    for (let i = 0; i < buffer.length; i ++) {
        // Where we are in the sound, in seconds.
        const t = i / SAMPLE_RATE;
        // The waves are visually at a very low frequency, we need to bump that up a bunch
        channel[i] += wave(baseFrequency * t);
    }
    const source = audioContext.createBufferSource();
    source.buffer = buffer;

    const gainNode = audioContext.createGain();
    gainNode.gain.setValueAtTime(baseVolume, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + decay);

    source.connect(gainNode);
    gainNode.connect(audioContext.destination);

    source.start();
    source.stop(audioContext.currentTime + decay);
}

export function playChirp(f0 = 200, f1 = 800, duration = 1.5) {
    const baseVolume = 0.6;
    const decay = duration + 0.5;
    
    const audioContext = getAudioContext();
    if (audioContext === null) {
        return false;
    }
    
    const numSamples = Math.floor(SAMPLE_RATE * duration);
    const buffer = audioContext.createBuffer(1, numSamples, SAMPLE_RATE);
    const channel = buffer.getChannelData(0);
    
    const chirpRate = (f1 - f0) / duration;
    
    for (let i = 0; i < numSamples; i++) {
        const t = i / SAMPLE_RATE;
        const instantFreq = f0 + chirpRate * t;
        const phase = 2 * Math.PI * (f0 * t + 0.5 * chirpRate * t * t);
        const envelope = Math.sin(Math.PI * t / duration);
        channel[i] = envelope * Math.sin(phase);
    }
    
    const source = audioContext.createBufferSource();
    source.buffer = buffer;
    
    const gainNode = audioContext.createGain();
    gainNode.gain.setValueAtTime(baseVolume, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + decay);
    
    source.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    source.start();
    source.stop(audioContext.currentTime + decay);
}

export function playLigoChirp(duration = 2) {
    const baseVolume = 0.7;
    const decay = duration + 0.3;
    
    const audioContext = getAudioContext();
    if (audioContext === null) {
        return false;
    }
    
    const numSamples = Math.floor(SAMPLE_RATE * duration);
    const buffer = audioContext.createBuffer(1, numSamples, SAMPLE_RATE);
    const channel = buffer.getChannelData(0);
    
    const baseFreq = 100;
    const maxFreq = 500;
    let phase = 0;
    
    for (let i = 0; i < numSamples; i++) {
        const t = i / SAMPLE_RATE;
        const normalizedT = t / duration;
        
        const freq = baseFreq + (maxFreq - baseFreq) * Math.pow(normalizedT, 3);
        phase += 2 * Math.PI * freq / SAMPLE_RATE;
        
        const amplitude = 0.3 + 0.7 * normalizedT;
        const envelope = Math.sin(Math.PI * normalizedT);
        channel[i] = amplitude * envelope * Math.sin(phase);
    }
    
    const source = audioContext.createBufferSource();
    source.buffer = buffer;
    
    const gainNode = audioContext.createGain();
    gainNode.gain.setValueAtTime(baseVolume, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + decay);
    
    source.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    source.start();
    source.stop(audioContext.currentTime + decay);
}
